// Defensive Telegram Mini App SDK wrapper.
// Every SDK call is guarded — browser/dev or older clients fall through silently.
// Spec: README.md §8.1; DESIGN.md §1.4 (Defensive UX); .cursorrules § "What NOT to do".
//
// Safe-area: handled by CSS `env(safe-area-inset-*)` plus `viewport-fit=cover` in index.html.
// Telegram WebView surfaces insets via the same CSS env() vars on iOS, so there's no need
// to wire the SDK-level `safeAreaInsets` signal here. If a finer-grained insets-changed
// observer is needed later (e.g. when the user pulls the WebView header), it can be added
// without touching consumers — they read `var(--safe-top)` / `var(--safe-bottom)` from CSS.

import { init, miniApp, viewport, requestFullscreen } from '@telegram-apps/sdk-react';

const SURFACE_MAIN = '#1F1F1F';

let initPromise: Promise<void> | null = null;

export function initTelegram(): Promise<void> {
  if (!initPromise) {
    initPromise = runInit();
  }
  return initPromise;
}

async function runInit(): Promise<void> {
  try {
    await init();
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[telegram] init() failed — running in browser mode', err);
    }
    return;
  }

  trySync(() => viewport.expand());

  if (trySync(() => requestFullscreen.isAvailable())) {
    try {
      await requestFullscreen();
    } catch {
      // viewport.expand() above is already our fallback.
    }
  }

  trySync(() => miniApp.setHeaderColor(SURFACE_MAIN));
  trySync(() => miniApp.setBackgroundColor(SURFACE_MAIN));
}

function trySync<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}
